import React, { useEffect, useState, useRef } from "react";
import { navigate } from "raviger";
import { toast } from 'react-toastify';
import logo from './../../../assets/img/logo.png';
import "./Authenticate.css";
import FormRenderer, { ComponentMapper, FormTemplate } from "../../common/FormRenderer";
import PageLoader from "../../shared/PageLoader";
import { LOGIN_FIELDS } from "./Authenticate.constants";
import { useSSOCheck, useSSOInit } from "../../../backend/auth";
import { getBasePath } from "../../../App";
import { AxiosError } from "axios";
import ssoLogin from "./../../../assets/img/ssoLogin.png";
import { setUserSession } from "../../../backend/auth";

function SSOLogin() {
    const [form, setForm] = useState<any>({});
    const [code, setCode] = useState('');
    const ssoPopupRef = useRef<Window | null>(null);
    const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;

            if (event.data && typeof event.data === 'object') {
                if (event.data.type === 'SSO_SUCCESS') {
                    if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                        ssoPopupRef.current.close();
                    }

                    if (event.data.token) {
                        try {
                            setUserSession(event.data.token);
                            navigate('/');
                            window.location.reload();
                        } catch (error) {
                            toast.error('Failed to process SSO token');
                        }
                    }
                } else if (event.data.type === 'SSO_ERROR') {
                    if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                        ssoPopupRef.current.close();
                    }

                    toast.error(event.data.error || 'SSO login failed. Please try again.');
                }
            }
        };

        messageHandlerRef.current = handleMessage;

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
            if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                ssoPopupRef.current.close();
            }
        };
    }, []);
    
    const schema = {
        fields: [LOGIN_FIELDS.COMPANY_CODE]
    };

    const { checkSSO, isLoading: checkingSSO } = useSSOCheck(({ sso_available }: any) => {
        if (sso_available) {
            // Initiate SSO if available
            const redirectUrl = `${getBasePath()}/auth/callback`;
            initSSO({ code, redirectUrl });
        } else {
            toast.error('SSO is not enabled for this company');
        }
    }, errorCallback);

    const { initSSO, isLoading: initiatingSSO } = useSSOInit(({ redirect_url }: any) => {
        if (redirect_url) {
            if (ssoPopupRef.current && !ssoPopupRef.current.closed) {
                ssoPopupRef.current.close();
            }

            const windowWidth = 600;
            const windowHeight = 700;
            const left = (window.screen.width - windowWidth) / 2;
            const top = (window.screen.height - windowHeight) / 2;

            const popup = window.open(
                redirect_url,
                'EzycompSSOLogin',
                `width=${windowWidth},height=${windowHeight},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
            );

            if (popup) {
                ssoPopupRef.current = popup;

                setTimeout(() => {
                    if (!popup || popup.closed) {
                        toast.error('Popup was blocked. Please allow popups for this site.');
                    }
                }, 1000);

                const checkPopupInterval = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(checkPopupInterval);
                        ssoPopupRef.current = null;
                    }
                }, 1000);
            } else {
                toast.error('Failed to open SSO login window. Please check if popups are blocked.');
            }
        } else {
            toast.error('Failed to initiate SSO login');
        }
    }, errorCallback);

    function errorCallback() {
        toast.error('An error occurred. Please try again.');
    }

    function handleSubmit({ code }: any) {
        setCode(code);
        checkSSO({ code });
    }

    function goBackToLogin() {
        navigate('/login');
    }

    return (
        <>
            <div className="row m-0 overflow-hidden" style={{height: "100vh"}}>
                <div className="col-md-8 p-0 bannerSlider d-flex align-items-center justify-content-center" style={{backgroundColor: "#005ae6"}}>
                    <img className="d-block" src={ssoLogin} alt="First slide" style={{height: "80vh"}} />
                    <div className="text-center text-white" style={{position: "absolute", top: "80px"}}>
                        <h2>Single Sign-On Login</h2>
                        <p>Enter your company code to log in with SSO</p>
                    </div>
                </div>

                <div className="col-md-4 loginSection px-5 py-3">
                    <div className="d-flex flex-column h-100 justify-content-between align-items-center mt-5">
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            <div className="navbar-brand p-0 text-center mb-4">
                                <img src={logo} alt="Logo" width="344" />
                            </div>
                            <div className="col-9" style={{ minHeight: '200px' }}>
                                <FormRenderer 
                                    FormTemplate={FormTemplate}
                                    initialValues={{ submitBtnText: 'Continue with SSO', fullWidth: false }}
                                    componentMapper={ComponentMapper}
                                    schema={schema}
                                    debug={setForm}
                                    onSubmit={handleSubmit}
                                    clearOnUnmount={true}
                                />
                                <div className="text-center mt-3">
                                    <button 
                                        type="button"
                                        className="btn btn-link text-secondary"
                                        onClick={goBackToLogin}
                                    >
                                        ← Back to regular login
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="">
                            <div className="card"></div>
                        </div>
                    </div>
                </div>
            </div>
            {
                (checkingSSO || initiatingSSO) && <PageLoader>
                    {
                        checkingSSO && <p>Checking SSO availability...</p>
                    }
                    {
                        initiatingSSO && <p>Initiating SSO login...</p>
                    }
                </PageLoader>
            }
        </>
    );
}

export default SSOLogin;
